import type { CollectionsApi } from '@wix/wix-code-collections-api'
import type {
  EditorSDK,
  FedopsLogger,
  I18n,
} from '@wix/wix-data-client-common-standalone'
import type { PageRef } from '@wix/editor-platform-sdk-types'
import { UNDO_REDO_LABELS, type AppApi } from '@wix/wix-data-client-common'
import type { HttpClient } from '@wix/http-client'
import { withProgressBar } from '../utils/withProgressBar'
import { getInitialPageDataAndConnections } from './getInitialPageDataAndConnections'
import { updateDatasetAndBindings } from './updateDatasetAndBindings'
import { prepareRouteData } from './prepareRouteData'
import { addMainSlugFieldIfNotExists } from '../../slug/addSlugField'
import { deleteItemPageSeoSchema } from '../itemPageSeoSchema'

interface Params {
  pageRef: PageRef
  newCollectionId: string
  newRouterPrefix: string
  fieldMap: Record<string, string>
  editorSdkProxy: EditorSDK
  collectionsApi: CollectionsApi
  appApi: AppApi
  httpClient: HttpClient
  i18n: I18n
  fedopsLogger: FedopsLogger
}

const replaceCollection = async ({
  pageRef,
  newCollectionId,
  newRouterPrefix,
  fieldMap,
  editorSdkProxy,
  collectionsApi,
  appApi,
  httpClient,
}: Omit<Params, 'i18n' | 'fedopsLogger'>) => {
  const { schema, routerRef, controller, router } =
    await getInitialPageDataAndConnections(
      pageRef,
      newCollectionId,
      editorSdkProxy,
      collectionsApi,
    )

  const { updatedRouterConfig } = await prepareRouteData({
    oldRouter: router,
    pageRef,
    newCollectionId,
    editorSdkProxy,
  })

  await addMainSlugFieldIfNotExists({
    collectionsApi,
    collectionId: newCollectionId,
  })

  await updateDatasetAndBindings(editorSdkProxy, appApi, {
    controllerRef: controller.ref,
    routerRef,
    newRouterPrefix,
    updatedRouterConfig,
    schema,
    fieldMap,
  })

  deleteItemPageSeoSchema({
    httpClient,
    pageId: pageRef.id,
  })

  await appApi.handleChangedRouters({ pageRefs: [pageRef] })

  await editorSdkProxy.history.add({
    label: UNDO_REDO_LABELS.UPDATE_ROUTERS,
    params: { pageRefs: [pageRef] },
  })
}

const interactionName = 'replaceDynamicPageCollection'

export const replaceDynamicPageCollection = async ({
  pageRef,
  newCollectionId,
  fieldMap,
  newRouterPrefix,
  editorSdkProxy,
  collectionsApi,
  appApi,
  httpClient,
  i18n,
  fedopsLogger,
}: Params) => {
  fedopsLogger.interactionStarted(interactionName)

  await withProgressBar(
    {
      editorSdkProxy,
      title: i18n.t('Replacing_Dynamic_Page_Collection_Progress_Bar_Title'),
    },
    () =>
      replaceCollection({
        pageRef,
        newCollectionId,
        newRouterPrefix,
        fieldMap,
        editorSdkProxy,
        collectionsApi,
        appApi,
        httpClient,
      }),
  )

  fedopsLogger.interactionEnded(interactionName)

  await editorSdkProxy.pages.navigateTo({ pageRef })

  return {}
}
